import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const TemperingForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [grainLots, setGrainLots] = useState<any[]>([]);
  const [silos, setSilos] = useState<any[]>([]);

  const [form, setForm] = useState({
    recordNumber: '',
    grainLotId: '',
    grainLotNumber: '',
    siloId: '',
    batchQtyMt: '',
    initialMoisture: '',
    targetMoisture: '',
    operatorName: '',
    notes: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [lotsRes, silosRes] = await Promise.all([
          millingApi.getGrainLots({ status: 'in_silo' }).catch(() => ({ data: [] })),
          millingApi.getSilos().catch(() => ({ data: [] })),
        ]);
        setGrainLots(lotsRes.data || lotsRes || []);
        setSilos(silosRes.data || silosRes || []);

        if (isEdit) {
          const rec = await millingApi.getTemperingRecord(id!);
          setForm({
            recordNumber: rec.recordNumber || '',
            grainLotId: rec.grainLotId || '',
            grainLotNumber: rec.grainLotNumber || '',
            siloId: rec.siloId || '',
            batchQtyMt: rec.batchQtyMt?.toString() || '',
            initialMoisture: rec.initialMoisture?.toString() || '',
            targetMoisture: rec.targetMoisture?.toString() || '',
            operatorName: rec.operatorName || '',
            notes: rec.notes || '',
          });
        }
      } catch {
        setError('Failed to load data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit]);

  const handleLotChange = (lotId: string) => {
    const lot = grainLots.find((l) => l.id === lotId);
    setForm((f) => ({
      ...f,
      grainLotId: lotId,
      grainLotNumber: lot?.lotNumber || '',
      siloId: lot?.siloId || f.siloId,
      initialMoisture: lot?.moisturePercent?.toString() || f.initialMoisture,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.recordNumber.trim()) { setError('Record number is required.'); return; }
    if (!form.grainLotId) { setError('Grain lot is required.'); return; }
    if (!form.batchQtyMt) { setError('Batch quantity is required.'); return; }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        batchQtyMt: Number(form.batchQtyMt),
        initialMoisture: form.initialMoisture ? Number(form.initialMoisture) : undefined,
        targetMoisture: form.targetMoisture ? Number(form.targetMoisture) : undefined,
        siloId: form.siloId || undefined,
      };

      if (isEdit) {
        await millingApi.updateTemperingRecord(id!, payload);
      } else {
        await millingApi.createTemperingRecord(payload);
      }
      navigate('/milling/tempering');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button onClick={() => navigate('/milling/tempering')}>← Back</Button>
        <Typography variant="h5">{isEdit ? 'Edit Tempering Record' : 'New Tempering Record'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Record Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Record Number *"
                  fullWidth
                  value={form.recordNumber}
                  onChange={(e) => setForm((f) => ({ ...f, recordNumber: e.target.value }))}
                  placeholder="e.g. TEMP-2024-001"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Operator Name"
                  fullWidth
                  value={form.operatorName}
                  onChange={(e) => setForm((f) => ({ ...f, operatorName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Grain Lot *</InputLabel>
                  <Select
                    value={form.grainLotId}
                    label="Grain Lot *"
                    onChange={(e) => handleLotChange(e.target.value)}
                  >
                    {grainLots.length === 0 && (
                      <MenuItem value="" disabled>No approved grain lots in silos</MenuItem>
                    )}
                    {grainLots.map((lot) => (
                      <MenuItem key={lot.id} value={lot.id}>
                        {lot.lotNumber} — {lot.grainType} ({lot.availableQtyMt} MT available)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Silo</InputLabel>
                  <Select
                    value={form.siloId}
                    label="Silo"
                    onChange={(e) => setForm((f) => ({ ...f, siloId: e.target.value }))}
                  >
                    <MenuItem value="">None</MenuItem>
                    {silos.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.siloCode} — {s.siloName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Batch Quantity (MT) *"
                  type="number"
                  fullWidth
                  value={form.batchQtyMt}
                  onChange={(e) => setForm((f) => ({ ...f, batchQtyMt: e.target.value }))}
                  inputProps={{ step: 0.001, min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Initial Moisture (%)"
                  type="number"
                  fullWidth
                  value={form.initialMoisture}
                  onChange={(e) => setForm((f) => ({ ...f, initialMoisture: e.target.value }))}
                  inputProps={{ step: 0.01, min: 0, max: 30 }}
                  helperText="Pre-tempering moisture"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Target Moisture (%)"
                  type="number"
                  fullWidth
                  value={form.targetMoisture}
                  onChange={(e) => setForm((f) => ({ ...f, targetMoisture: e.target.value }))}
                  inputProps={{ step: 0.01, min: 0, max: 30 }}
                  helperText="Typically 15–16.5% for wheat"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update Record' : 'Create Record'}
          </Button>
          <Button onClick={() => navigate('/milling/tempering')}>Cancel</Button>
        </Box>
      </form>
    </Box>
  );
};

export default TemperingForm;
